import React, { useState, useRef, useEffect } from "react";
import {
  Search,
  Bell,
  User,
  Pencil,
  Settings,
  KeyRound,
  LogOut,
  X,
  SlidersHorizontal,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../store/reducers/auth";
import { useGetNotificationsQuery } from "../store/services/notification";
import { formatTime, formatDate } from "../utils/commonMethods";
import BasicDialog from "./BasicDialog";
import AdvanceSearch from "./AdvanceSearch";
import { API_AXIOS } from "../http/interceptor";
import { toast } from "react-toastify";
import Apis from "../utils/apis";

const ModernHeader = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { auth } = useSelector((state) => state.auth);

  // Helper to determine page title based on path
  const getPageTitle = (path) => {
    const route = path.split("/")[1] || "Dashboard";
    return route.charAt(0).toUpperCase() + route.slice(1);
  };

  const title = getPageTitle(pathname);
  const personalizedGreeting = `Good Morning, ${auth?.name || "User"}`; // Simple greeting logic

  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [openAdvanceSearch, setOpenAdvanceSearch] = useState(false); // Advance search state
  const [isPasswordChanging, setIsPasswordChanging] = useState(false);
  const [profileImage, setProfileImage] = useState(auth?.image || null);
  const [uploadError, setUploadError] = useState(null);

  const notificationsRef = useRef(null);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  // change password form values
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // change password submit handle
  const handleSubmit = async () => {
    if (
      !formData.newPassword ||
      !formData.confirmPassword ||
      !formData.currentPassword
    ) {
      toast.error("All fields are mandatory!");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsPasswordChanging(true);

    try {
      const { data } = await API_AXIOS.post(
        `${Apis.changePassword}/${auth?._id}`,
        { ...formData, oldPassword: formData.currentPassword },
      );

      if (data) {
        toast.success(data);
        localStorage.clear();
        navigate(0);
      }
    } catch (error) {
      toast.error(error?.response?.data || "Something went wrong");
    }

    setIsPasswordChanging(false);
  };

  // Fermer les dropdowns si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setIsNotificationsOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Notifications Logic
  const { data: notificationsData } = useGetNotificationsQuery(
    `user/getNotificationsById/${auth?._id}?page=1`,
  );

  const notifications = (notificationsData || []).map((n) => ({
    id: n._id,
    read: n.seen,
    title: n.type,
    message: n.message,
    time:
      formatDate(n.time) === formatDate(new Date())
        ? formatTime(n.time)
        : formatDate(n.time),
  }));
  const unreadCount = notifications.filter((n) => !n.read).length;

  const onSearchClick = () => {
    setOpenAdvanceSearch(true);
  };

  const handleAdvanceSearchClose = () => {
    setOpenAdvanceSearch(false);
  };

  const onNavigate = (dest) => {
    navigate("/" + dest);
  };

  const handleLogoutClick = () => {
    localStorage.clear();
    dispatch(logout());
    navigate("/");
  };

  const handleProfileImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        setUploadError("File size too large (max 1MB)");
        return;
      }
      // Mock upload for now
      setProfileImage(URL.createObjectURL(file));
      setUploadError(null);
    }
  };

  const onManageProfile = () => {
    // Navigate to profile management
    console.log("Manage profile");
  };

  return (
    <header className="h-16 bg-white border-b border-[#739fe1]/10 flex items-center justify-between px-6 shadow-sm flex-shrink-0 relative mb-1 z-10">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#f9fbfd] via-white to-[#f9fbfd] opacity-50 animate-gradient" />

      {/* Left: Title */}
      <div className="relative z-10 animate-slide-in-left">
        <h2 className="text-xl text-[#2e67be] tracking-tight font-semibold">
          {title}
        </h2>
        <p className="text-xs text-[#739fe1]">{personalizedGreeting}</p>
      </div>

      {/* Right: Search + Notifications + Profile */}
      <div className="relative z-10 flex items-center gap-4 animate-slide-in-right">
        {/* Search Bar */}
        <button
          onClick={onSearchClick}
          className="flex items-center gap-2 h-9 px-4 bg-[#f9fbfd] border border-[#739fe1]/20 rounded-lg text-sm text-[#2e67be] hover:border-[#4985f2]/40 hover:bg-white transition-all cursor-pointer group"
        >
          <SlidersHorizontal className="w-4 h-4 text-[#739fe1] group-hover:text-[#4985f2] transition-all" />
          <span>Advance Filter</span>
        </button>

        {/* Notifications Dropdown */}
        <div className="relative" ref={notificationsRef}>
          <button
            onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2e67be] to-[#4985f2] flex items-center justify-center hover:shadow-lg hover:shadow-[#2e67be]/30 transition-all active:scale-95 relative cursor-pointer"
          >
            <Bell className="w-5 h-5 text-white" strokeWidth={2} />
            {unreadCount > 0 && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                {unreadCount}
              </div>
            )}
          </button>

          {/* Notifications Dropdown Menu */}
          {isNotificationsOpen && (
            <div className="absolute right-0 top-14 w-96 bg-white rounded-xl border border-gray-200 shadow-xl z-30 max-h-[500px] flex flex-col">
              {/* Notifications Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h4 className="text-[#2e67be]">Notifications</h4>
                {unreadCount > 0 && (
                  <span className="text-xs text-[#739fe1]">
                    {unreadCount} unread
                  </span>
                )}
              </div>

              {/* Notifications List - Scrollable */}
              <div className="overflow-y-auto max-h-[350px]">
                {notifications.length > 0 ? (
                  notifications.map((notification) => (
                    <button
                      key={notification.id}
                      className={`w-full flex gap-3 px-4 py-3 border-b border-gray-50 transition-all text-left ${
                        notification.read
                          ? "hover:bg-[#f9fbfd]"
                          : "bg-[#e3f2fd]/30 hover:bg-[#e3f2fd]/50"
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          notification.read ? "bg-gray-300" : "bg-[#4985f2]"
                        }`}
                      ></div>
                      <div className="flex-1 min-w-0">
                        <h5
                          className={`text-sm ${
                            notification.read
                              ? "text-gray-700"
                              : "text-[#2e67be]"
                          }`}
                        >
                          {notification.title}
                        </h5>
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-[#739fe1] mt-1">
                          {notification.time}
                        </p>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No notifications
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-3 border-t border-gray-200">
                <button
                  onClick={() => {
                    setIsNotificationsOpen(false);
                    onNavigate?.("notifications");
                  }}
                  className="w-full text-center text-sm text-[#4985f2] hover:text-[#2e67be] transition-all"
                >
                  View All Notifications
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Profile Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2e67be] to-[#4985f2] flex items-center justify-center hover:shadow-lg hover:shadow-[#2e67be]/30 transition-all active:scale-95 overflow-hidden"
          >
            {profileImage ? (
              <img
                src={profileImage}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="w-5 h-5 text-white" strokeWidth={2} />
            )}
          </button>

          {/* Dropdown Menu */}
          {isProfileOpen && (
            <div className="absolute right-0 top-14 w-80 bg-white rounded-xl border border-gray-200 shadow-xl z-30">
              {/* User Info Section */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#2e67be] to-[#4985f2] flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {profileImage ? (
                        <img
                          src={profileImage}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User
                          className="w-6 h-6 text-white"
                          strokeWidth={2.5}
                        />
                      )}
                    </div>
                    {/* Pencil Icon Button */}
                    <button
                      onClick={handleProfileImageClick}
                      className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-white border-2 border-[#4985f2] flex items-center justify-center hover:bg-[#f9fbfd] transition-all active:scale-95 group shadow-sm"
                      title="Upload profile picture (Max 1 MB)"
                    >
                      <Pencil
                        className="w-2.5 h-2.5 text-[#4985f2] group-hover:text-[#2e67be]"
                        strokeWidth={2.5}
                      />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[#2e67be] truncate">
                      {auth?.name} {auth?.lastName}
                    </h4>
                    <p className="text-sm text-gray-600 truncate">
                      {auth?.email}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 capitalize">
                      ({auth?.role})
                    </p>
                  </div>
                </div>
                {uploadError && (
                  <p className="text-xs text-red-500 mt-2">{uploadError}</p>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>

              {/* Menu Options */}
              <div className="p-2">
                <button
                  onClick={() => {
                    setIsChangePasswordOpen(true);
                    setIsProfileOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-[#f9fbfd] hover:text-[#2e67be] transition-all"
                >
                  <KeyRound
                    className="w-5 h-5 text-[#739fe1]"
                    strokeWidth={2}
                  />
                  <span className="text-sm">Change Password</span>
                </button>
                <button
                  onClick={handleLogoutClick}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all"
                >
                  <LogOut className="w-5 h-5 text-red-500" strokeWidth={2} />
                  <span className="text-sm">Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {isChangePasswordOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2e67be] to-[#4985f2] flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-xl text-[#2e67be]">Change Password</h3>
              </div>
              <button
                onClick={() => setIsChangePasswordOpen(false)}
                className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-all"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  placeholder="Enter current password"
                  className="w-full h-11 px-4 bg-[#f9fbfd] border border-[#739fe1]/20 rounded-lg text-sm text-[#2e67be] placeholder:text-[#739fe1]/50 focus:outline-none focus:ring-2 focus:ring-[#4985f2]/30 focus:border-[#4985f2] transition-all"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  className="w-full h-11 px-4 bg-[#f9fbfd] border border-[#739fe1]/20 rounded-lg text-sm text-[#2e67be] placeholder:text-[#739fe1]/50 focus:outline-none focus:ring-2 focus:ring-[#4985f2]/30 focus:border-[#4985f2] transition-all"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter new password"
                  className="w-full h-11 px-4 bg-[#f9fbfd] border border-[#739fe1]/20 rounded-lg text-sm text-[#2e67be] placeholder:text-[#739fe1]/50 focus:outline-none focus:ring-2 focus:ring-[#4985f2]/30 focus:border-[#4985f2] transition-all"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setIsChangePasswordOpen(false)}
                className="flex-1 h-11 px-6 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isPasswordChanging}
                className="flex-1 h-11 px-6 rounded-lg bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white hover:shadow-lg hover:shadow-[#2e67be]/30 transition-all font-medium"
              >
                {isPasswordChanging ? "Changing..." : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Changing Success Animation Overlay */}
      {isPasswordChanging && (
        <div className="fixed inset-0 bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500 z-[100] flex flex-col items-center justify-center animate-[fadeIn_0.3s_ease-out]">
          {/* Animated Success Icon */}
          <div className="relative mb-8">
            <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center animate-[pulse_1.5s_ease-in-out_infinite]">
              <KeyRound
                className="w-12 h-12 text-white animate-[bounce_1s_ease-in-out_infinite]"
                strokeWidth={2}
              />
            </div>
            {/* Checkmark Circle Animation */}
            <div className="absolute inset-0 w-24 h-24 rounded-full border-4 border-white/30 border-t-white animate-spin"></div>
            {/* Success Checkmark */}
            <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-white flex items-center justify-center animate-[scaleIn_0.5s_ease-out_0.3s_both]">
              <svg
                className="w-6 h-6 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={3}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>

          {/* Text */}
          <div className="text-center space-y-2">
            <h3 className="text-2xl text-white animate-[fadeIn_0.5s_ease-out]">
              Password Changed!
            </h3>
            <p className="text-white/80 text-sm animate-[fadeIn_0.7s_ease-out]">
              Your password has been updated successfully
            </p>
          </div>

          {/* Success Progress Bar */}
          <div className="mt-8 w-64 h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full animate-[slideRight_2s_ease-out]"
              style={{ width: "100%" }}
            ></div>
          </div>

          {/* Decorative Elements */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-3xl animate-[float_3s_ease-in-out_infinite]"></div>
          <div
            className="absolute bottom-20 right-20 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-[float_4s_ease-in-out_infinite]"
            style={{ animationDelay: "1s" }}
          ></div>

          {/* Confetti-like decorative circles */}
          <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-white/50 rounded-full animate-[ping_2s_ease-out_infinite]"></div>
          <div className="absolute top-1/3 right-1/4 w-2 h-2 bg-white/50 rounded-full animate-[ping_2s_ease-out_infinite_0.5s]"></div>
          <div className="absolute bottom-1/4 left-1/3 w-4 h-4 bg-white/50 rounded-full animate-[ping_2s_ease-out_infinite_1s]"></div>
        </div>
      )}

      {/* Advance Search Dialog */}
      <BasicDialog
        fullScreen={false}
        sx={{
          "& .MuiDialog-paper": {
            width: { xs: "calc(100vw - 16px)", sm: "96vw" },
            height: { xs: "calc(100dvh - 16px)", sm: "88dvh" },
            maxWidth: "none",
            maxHeight: { xs: "calc(100dvh - 16px)", sm: "88dvh" },
            margin: 0,
            borderRadius: "11.7px",
            border: "1px solid #2c67c6",
          },
          "& .MuiDialogContent-root": {
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
            p: { xs: 2, sm: 3 },
          },
          "& .MuiDialogContent-root::-webkit-scrollbar": {
            display: "none",
          },
        }}
        open={openAdvanceSearch}
        handleClose={handleAdvanceSearchClose}
      >
        <AdvanceSearch
          handleClose={handleAdvanceSearchClose}
          title={"Advance Filter"}
        />
      </BasicDialog>
    </header>
  );
};

export default ModernHeader;
